import { useParams, Navigate, Link } from "react-router-dom";
import { Check } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import PillButton from "../components/common/PillButton";
import CTASection from "../components/common/CTASection";
import { Icon } from "../lib/icon";
import { getSolution, solutions } from "../data/solutions";
import { useStaffing } from "../components/forms/StaffingProvider";

export default function SolutionDetail() {
  const { slug } = useParams();
  const solution = getSolution(slug);
  const { open } = useStaffing();
  if (!solution) return <Navigate to="/solutions" replace />;

  const isWorkforce = solution.slug === "workforce-transformation";
  const others = solutions.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO title={`${solution.name} Solutions`} description={solution.seo} path={`/solutions/${slug}`} />
      <PageHero eyebrow="Solution" title={solution.name} subtitle={solution.tagline} image={solution.image} neural>
        {isWorkforce ? (
          <PillButton onClick={open} size="lg" testId="solution-hero-cta">{solution.cta}</PillButton>
        ) : (
          <PillButton to="/contact" size="lg" testId="solution-hero-cta">{solution.cta}</PillButton>
        )}
      </PageHero>

      <section className="px-6 py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-sky-400">
              <Icon name={solution.icon} className="h-7 w-7" />
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white lg:text-3xl">The Problem</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{solution.problem}</p>
            <h2 className="mt-10 font-heading text-2xl font-bold tracking-tight text-white lg:text-3xl">The AMVI Approach</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{solution.approach}</p>
          </Reveal>

          <div className="space-y-6">
            <Reveal>
              <div className="rounded-2xl border border-white/10 bg-ink-800/50 p-8">
                <h3 className="font-heading text-xl font-bold tracking-tight text-white">Capabilities</h3>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {solution.capabilities.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-400">
                        <Check className="h-3 w-3" />
                      </span>
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-ink-800/80 to-ink-800/30 p-8">
                <h3 className="font-heading text-xl font-bold tracking-tight text-white">Outcomes</h3>
                <ul className="mt-6 grid gap-3">
                  {solution.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 px-6 py-16">
        <div className="mx-auto max-w-7xl">
          <h3 className="font-heading text-xl font-bold tracking-tight text-white">Explore other solutions</h3>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/solutions/${o.slug}`}
                className="group rounded-2xl border border-white/10 bg-ink-800/40 p-6 transition-colors hover:border-sky-400/40"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sky-400">
                  <Icon name={o.icon} className="h-5 w-5" />
                </div>
                <div className="font-heading font-semibold text-white group-hover:text-sky-300">{o.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
